import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useDisableAccountMutation } from '@/services/user/queries';
import { useAuthStore } from '@/store/auth';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

// Password is required for UI but NOT sent to API
const formSchema = z.object({
  password: z.string().min(1, 'Password is required')
});

interface DisableAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DisableAccountDialog({
  open,
  onOpenChange
}: DisableAccountDialogProps) {
  const userId = useAuthStore((state) => state.user?.id) || '';
  const clearAuthData = useAuthStore((state) => state.clearAuthData);
  const navigate = useNavigate();
  const posthog = usePostHog();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: ''
    }
  });

  const { mutate: disableAccount, isPending: isLoading } = useDisableAccountMutation(
    userId,
    () => {
      posthog?.capture('form_submitted', { form_name: 'disable_account', success: true });
      posthog?.capture('modal_closed', { modal_name: 'disable_account', completed: true });
      toast.success('Account disabled successfully.');
      form.reset();
      onOpenChange(false);
      clearAuthData();
      setTimeout(() => navigate('/sign-in', { replace: true }), 0);
    },
    (error) => {
      posthog?.capture('form_submitted', { form_name: 'disable_account', success: false });
      toast.error(error?.response?.data?.message || 'Failed to disable account.');
    }
  );

  async function onSubmit(values: z.infer<typeof formSchema>) {
    disableAccount(values.password);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Disable Account</DialogTitle>
          <DialogDescription>
            Your account will be temporarily disabled. You can reactivate it by
            logging in later.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            {/* Password field (UI-only) */}
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <Input type='password' {...field} placeholder='Enter your password to confirm' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  posthog?.capture('modal_closed', { modal_name: 'disable_account', completed: false });
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                variant='destructive'
                className='bg-orange-600 hover:bg-orange-700'
                disabled={isLoading}
                trackingEvent='disable_account_submitted'
              >
                {isLoading ? 'Processing...' : 'Disable Account'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}